import { Injectable, OnDestroy, effect, DestroyRef, inject, EffectRef } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TtsStreamService } from './tts-stream.service';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';
import { AssemblyService } from 'src/app/pages/learner/call/library/assembly/assembly.service';
import { CallService } from './call.service';
import { LoggerService } from '../logger/logger.service';
import { AppConstants } from 'src/app/app-constants';
import { IScormLearnerContext } from 'src/app/interfaces/iscorm-learner-context';

export enum ConversationState {
  IDLE = 'idle',
  INITIALIZING = 'initializing',
  AGENT_SPEAKING = 'agent_speaking',
  LISTENING = 'listening',
  USER_SPEAKING = 'user_speaking',
  PROCESSING = 'processing',
  ENDED = 'ended',
  ERROR = 'error',
}

export interface IChatMessage {
  role: 'agent' | 'user';
  content: string;
  timestamp: Date;
}

export interface ISegmentProvider {
  getUserSpokenResponse(): string;
  getCallLearnUuid(): string;
  // SCORM providers return their live learner location; when present the turn routes to processContentQuestion instead of processUserResponse. Learn providers omit it.
  getLearnerContext?(): IScormLearnerContext | null;
}

/**
 * AudioCallControlService - Orchestrates audio calls
 *
 * This service coordinates between:
 * - AssemblyService (Speech-to-Text)
 * - TtsStreamService (Text-to-Speech from backend stream)
 * - CallStoreService (State management)
 *
 * It manages:
 * - Conversation state (who's speaking)
 * - Turn-taking logic (pause STT when agent speaks)
 * - Silence detection (configurable timeout)
 * - Conversation history
 */
@Injectable({
  providedIn: 'root',
})
export class AudioCallControlService implements OnDestroy {
  private static readonly HARDCODED_CALL_UUID = '69737c06-86ec-40d5-acc9-3565cfd29045';
// Complete silence → RMS ≈ 0
// Background noise → RMS ≈ 100–400
// Normal speech → RMS ≈ 800–3000+
// That's why the threshold is SILENCE_RMS_THRESHOLD = 800 — anything at or above 800 is treated as the user actively speaking.
  private static readonly SILENCE_RMS_THRESHOLD = 800;
  private readonly hardcodedCallUuid: string = AudioCallControlService.HARDCODED_CALL_UUID;

  private _state: ConversationState = ConversationState.IDLE;
  private _conversationHistory: IChatMessage[] = [];
  private _currentTranscript: string = '';
  private _partialTranscript: string = '';
  private _currentAgentSpeech: string = '';
  private _pendingWelcomeMessage: string = '';
  private turnCompletionTimer: ReturnType<typeof setTimeout> | null = null;
  private micSilenceTimer: ReturnType<typeof setTimeout> | null = null;
  private failsafeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastProcessedTranscript: string = '';
  private isWatchingTranscripts: boolean = false;
  private _skipListenCycleOnNextSpeakEnd = false;
  private subscriptions: Subscription[] = [];
  private destroyRef = inject(DestroyRef);
  private effectRefs: EffectRef[] = [];
  private segmentProvider: ISegmentProvider | null = null;
  private readonly _stateChange$ = new Subject<ConversationState>();
  private readonly _agentMessage$ = new Subject<string>();
  private readonly _userMessage$ = new Subject<string>();
  private readonly _transcriptUpdate$ = new Subject<string>();
  private readonly _captionText$ = new Subject<string>();
  private readonly _error$ = new Subject<Error>();
  private readonly _callEnded$ = new Subject<void>();
  private readonly _cancelPendingResponse$ = new Subject<void>();
  private readonly _welcomeMessageDone$ = new Subject<void>();
  private _isFirstSpeakPending = false;

  public get onStateChange$(): Observable<ConversationState> {
    return this._stateChange$.asObservable();
  }

  /** Fires once, the first time the agent finishes speaking after startCall(). */
  public get onWelcomeMessageDone$(): Observable<void> {
    return this._welcomeMessageDone$.asObservable();
  }

  public get onAgentMessage$(): Observable<string> {
    return this._agentMessage$.asObservable();
  }

  public get onUserMessage$(): Observable<string> {
    return this._userMessage$.asObservable();
  }

  public get onTranscriptUpdate$(): Observable<string> {
    return this._transcriptUpdate$.asObservable();
  }

  /** Emits originalAgentResponse for caption display when agent replies (processUserResponse). */
  public get onCaptionText$(): Observable<string> {
    return this._captionText$.asObservable();
  }

  public get onError$(): Observable<Error> {
    return this._error$.asObservable();
  }

  public get onCallEnded$(): Observable<void> {
    return this._callEnded$.asObservable();
  }

  get state(): ConversationState {
    return this._state;
  }

  get conversationHistory(): IChatMessage[] {
    return [...this._conversationHistory];
  }

  get currentTranscript(): string {
    return this._currentTranscript;
  }

  get partialTranscript(): string {
    return this._partialTranscript;
  }

  get currentAgentSpeech(): string {
    return this._currentAgentSpeech;
  }

  get isAgentSpeaking(): boolean {
    return this._state === ConversationState.AGENT_SPEAKING;
  }

  get isListening(): boolean {
    return this._state === ConversationState.LISTENING ||
           this._state === ConversationState.USER_SPEAKING;
  }

  constructor(
    private readonly ttsStreamService: TtsStreamService,
    private readonly callStore: CallStoreService,
    private readonly assemblyService: AssemblyService,
    private readonly callService: CallService,
    private readonly logger: LoggerService
  ) {
    this.logger.setFileContext({ service: 'AudioCallControlService' });
    this.initialize();
  }

  private initialize(): void {
    this.ttsStreamService.onSpeakingStart$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleAgentStartSpeaking();
      });

    this.ttsStreamService.onSpeakingEnd$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleAgentStopSpeaking();
      });

    this.ttsStreamService.onError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((error) => {
        this.handleError(error);
      });

    this.assemblyService.onSpeechStarted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.turnCompletionTimer !== null) {
          this.logger.log('[AUDIO_CALL_CONTROL] SpeechStarted received within turn completion window — cancelling send');
          this.cancelTurnCompletionTimer();
        }
        this.cancelFailsafeTimer();
      });

    this.assemblyService.onAudioLevel$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rms) => this.handleAudioLevel(rms));

    this.setupTranscriptWatcher();

    this.logger.log('[AUDIO_CALL_CONTROL] Initialized');
  }

  private setupTranscriptWatcher(): void {
    const finalTranscriptEffect = effect(() => {
      const transcript = this.callStore.getSpeechTranscript();

      if (this.isWatchingTranscripts && transcript && transcript !== this.lastProcessedTranscript) {
        this.logger.log('[AUDIO_CALL_CONTROL] Final transcript detected', { transcript });
        this.lastProcessedTranscript = transcript;
        this.handleTranscriptUpdate(transcript, true);
      }
    });

    const partialTranscriptEffect = effect(() => {
      const partialTranscript = this.callStore.getSpeechTranscriptPartial();
      const partialExists = this.callStore.getPartialTranscriptExists();

      if (this.isWatchingTranscripts && partialExists && partialTranscript) {
        this.logger.log('[AUDIO_CALL_CONTROL] Partial transcript', { partialTranscript });
        this.handleTranscriptUpdate(partialTranscript, false);
      }
    });

    const userSpokeEffect = effect(() => {
      const userSpoke = this.callStore.getUserSpoke();

      if (this.isWatchingTranscripts && userSpoke > 0) {
        if (this._state === ConversationState.LISTENING) {
          this.setState(ConversationState.USER_SPEAKING);
        }
      }
    });

    this.effectRefs.push(finalTranscriptEffect, partialTranscriptEffect, userSpokeEffect);
    this.destroyRef.onDestroy(() => {
      this.cleanupEffects();
    });

    this.logger.log('[AUDIO_CALL_CONTROL] Transcript watcher set up');
  }

  private cleanupEffects(): void {
    this.effectRefs.forEach(effectRef => {
      if (effectRef && typeof effectRef.destroy === 'function') {
        effectRef.destroy();
      }
    });
    this.effectRefs = [];
  }

  private startWatchingTranscripts(): void {
    this.isWatchingTranscripts = true;
    this.lastProcessedTranscript = '';
    this.logger.log('[AUDIO_CALL_CONTROL] Started watching transcripts');
  }

  private stopWatchingTranscripts(): void {
    this.isWatchingTranscripts = false;
    this.logger.log('[AUDIO_CALL_CONTROL] Stopped watching transcripts');
  }

  /**
   * Start the audio call
   * @param greetingMessage Optional greeting message for the agent to speak
   */
  async startCall(greetingMessage?: string): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Starting call');

    try {
      this._skipListenCycleOnNextSpeakEnd = false;
      this.setState(ConversationState.INITIALIZING);
      this._conversationHistory = [];
      this.clearTranscriptState();
      this.cancelTurnCompletionTimer();
      this.cancelMicSilenceTimer();
      this.cancelFailsafeTimer();
      this.callStore.setIsVisualAidOpen(false);

      await this.assemblyService.init();
      await this.assemblyService.startTranscriber(true);

      const greeting =
        greetingMessage?.trim() ||
        this._pendingWelcomeMessage.trim() ||
        this.getGreeting();
      if (this._pendingWelcomeMessage) {
        this._pendingWelcomeMessage = '';
      }
      this._isFirstSpeakPending = true;
      this.agentSpeak(greeting);

      this.logger.log('[AUDIO_CALL_CONTROL] Call started successfully');
    } catch (error) {
      this.handleError(error as Error, 'Failed to start call');
      throw error;
    }
  }

  async endCall(): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Ending call');

    this._isFirstSpeakPending = false;
    this.stopWatchingTranscripts();
    this.cancelTurnCompletionTimer();
    this.cancelMicSilenceTimer();
    this.cancelFailsafeTimer();
    // Cancelling in-flight agent response
    this._cancelPendingResponse$.next();
    this.ttsStreamService.stop();
    await this.assemblyService.stop();
    this.clearTranscriptState();

    this.setState(ConversationState.ENDED);
    this._callEnded$.next();
  }

  /**
   * Make the agent speak a message (e.g. greeting or reply).
   * Uses the streaming service to request and play agent audio from the backend.
   */
  agentSpeak(text: string): void {
    if (!text.trim()) {
      this.logger.warn('[AUDIO_CALL_CONTROL] Empty text provided to agentSpeak');
      return;
    }

    this.logger.log('[AUDIO_CALL_CONTROL] Agent speaking', {
      textPreview: text.substring(0, 50) + '...',
    });

    this.callStore.setIsAvatarSpeaking(true);
    this.addMessage('agent', text);
    this._agentMessage$.next(text);
    this._currentAgentSpeech = text;

    this.playAgentAudioStream(text);
  }

  registerSegmentProvider(provider: ISegmentProvider): void {
    this.segmentProvider = provider;
    this.logger.log('[AUDIO_CALL_CONTROL] Segment provider registered');
  }

  unregisterSegmentProvider(): void {
    this.segmentProvider = null;
    this.logger.log('[AUDIO_CALL_CONTROL] Segment provider unregistered');
  }

  /**
   * Process user's utterance (called when silence is detected).
   * Calls processUserResponse to get agent reply text, then plays it via audioStream.
   * If a segment provider is registered, uses formatted segments; otherwise uses plain transcript.
   */
  processUserUtterance(transcript: string): void {
    if (!transcript.trim()) {
      this.logger.warn('[AUDIO_CALL_CONTROL] Empty transcript, skipping');
      return;
    }

    // Terminal state (error raised or call ended): do not start another turn — a trailing transcript/timer would mis-route after the error screen tears the component down.
    if (this._state === ConversationState.ERROR || this._state === ConversationState.ENDED) {
      this.logger.warn('[AUDIO_CALL_CONTROL] Ignoring utterance — call is in a terminal state', {
        state: this._state,
      });
      return;
    }

    const userSpokenResponse = this.segmentProvider
      ? this.segmentProvider.getUserSpokenResponse()
      : transcript;
    const callLearnUuid = this.segmentProvider
      ? this.segmentProvider.getCallLearnUuid()
      : undefined;

    let effectiveUserSpokenResponse = userSpokenResponse.trim();

    // If using segment provider but no segments available, fallback to transcript
    if (this.segmentProvider && !userSpokenResponse.trim()) {
      this.logger.warn(
        '[AUDIO_CALL_CONTROL] Segment provider returned empty response, falling back to transcript'
      );
      effectiveUserSpokenResponse = transcript.trim();
    }

    const practiceCallUuid =
      this.callStore.getPracticeCallUuid()?.trim() || this.hardcodedCallUuid;
    const callUuid = this.callStore.getCallUuid()?.trim() ?? '';

    if (!callUuid) {
      this.logger.error(
        '[AUDIO_CALL_CONTROL] Missing callUuid for processUserResponse',
        new Error('callUuid is required')
      );
      this.handleError(
        new Error('callUuid is required for processUserResponse'),
        'Missing callUuid'
      );
      this.setState(ConversationState.LISTENING);
      this.startWatchingTranscripts();
      return;
    }

    // SCORM providers supply a learnerContext; present routes to processContentQuestion, absent to processUserResponse.
    const learnerContext = this.segmentProvider?.getLearnerContext?.() ?? null;

    this.logger.log('[AUDIO_CALL_CONTROL] Processing user utterance', {
      transcript: transcript.substring(0, 50) + (transcript.length > 50 ? '...' : ''),
      hasSegmentProvider: !!this.segmentProvider,
      mode: learnerContext ? 'scorm' : 'learn',
      userSpokenResponse:
        effectiveUserSpokenResponse.substring(0, 50) +
        (effectiveUserSpokenResponse.length > 50 ? '...' : ''),
    });

    this.stopWatchingTranscripts();
    this.clearTranscriptState();
    this.setState(ConversationState.PROCESSING);
    this.addMessage('user', transcript);
    this._userMessage$.next(transcript);

    const response$ = learnerContext
      ? this.callService.processContentQuestion(
          practiceCallUuid,
          callUuid,
          effectiveUserSpokenResponse,
          learnerContext
        )
      : this.callService.processUserResponse(
          practiceCallUuid,
          callUuid,
          effectiveUserSpokenResponse,
          callLearnUuid
        );

    response$
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this._cancelPendingResponse$))
      .subscribe({
        next: (res) => {
          const agentText =
            res.pronunciationCorrectedResponse?.trim() ||
            res.originalAgentResponse?.trim() ||
            '';
          if (!agentText) {
            this.logger.warn(
              '[AUDIO_CALL_CONTROL] processUserResponse returned no agent text'
            );
            this.setState(ConversationState.LISTENING);
            this.startWatchingTranscripts();
            return;
          }
          this._captionText$.next(res.originalAgentResponse?.trim() ?? '');
          this.addMessage('agent', agentText);
          this._agentMessage$.next(agentText);
          this._currentAgentSpeech = agentText;
          this.playAgentAudioStream(agentText);
        },
        error: (err) => {
          // Failed turn = call-level error: surface it (onError$ → error screen) and halt the loop via handleError; do NOT re-arm listening (that mis-routed a trailing turn after teardown).
          this.handleError(err instanceof Error ? err : new Error(String(err)), 'Converse turn failed');
        },
      });
  }

  /**
   * Requests agent audio stream from the backend and plays it via TtsStreamService.
   * Used for both greeting/reply (agentSpeak) and after user utterance (processUserUtterance).
   */
  private playAgentAudioStream(text: string): void {
    const practiceCallUuid =
      this.callStore.getPracticeCallUuid()?.trim() || this.hardcodedCallUuid;
    const stream$ = this.callService.getAudioStream(practiceCallUuid, text);
    this.ttsStreamService.playStream(stream$);
  }

  /**
   * Handle incoming transcript from Assembly AI
   * Call this method when transcript updates are received
   * @param transcript The partial or final transcript
   * @param isFinal Whether this is a final transcript
   */
  handleTranscriptUpdate(transcript: string, isFinal: boolean): void {
    if (this._state === ConversationState.AGENT_SPEAKING ||
        this._state === ConversationState.PROCESSING ||
        this._state === ConversationState.ERROR ||
        this._state === ConversationState.ENDED) {
      return;
    }

    if (!isFinal) {
      // Partial: user is actively speaking — cancel any pending turn completion
      this.cancelTurnCompletionTimer();
      this._partialTranscript = transcript;
      this.setState(ConversationState.USER_SPEAKING);
      // Assembly is still processing — reset failsafe window if it's running
      if (this.failsafeTimer !== null) {
        this.logger.log(
          '[AUDIO_CALL_CONTROL] Partial received during failsafe window — resetting failsafe timer',
        );
        this.startFailsafeTimer();
      }
    } else {
      // Final (end_of_turn=true): Assembly responded — cancel failsafe and proceed normally
      this.cancelFailsafeTimer();
      this.cancelMicSilenceTimer();

      // Final (end_of_turn=true): open the turn completion window.
      // transcript from the effect is callStore.getSpeechTranscript() which is already
      // the cumulative join of all recognized segments — just replace, don't append.
      this._currentTranscript = transcript;
      this._partialTranscript = '';
      this.logger.log(
        '[AUDIO_CALL_CONTROL] Final transcript received, starting turn completion window',
        {
          transcript: this._currentTranscript,
        },
      );
      this.startTurnCompletionTimer();
    }

    this._transcriptUpdate$.next(this._currentTranscript || this._partialTranscript);
  }

  forceProcessTranscript(): void {
    this.cancelTurnCompletionTimer();
    this.cancelMicSilenceTimer();
    this.cancelFailsafeTimer();

    const transcript = this._currentTranscript || this._partialTranscript;
    if (transcript.trim()) {
      this.processUserUtterance(transcript.trim());
    }
  }

  async setMicMuted(muted: boolean): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Mic muted', { muted });

    if (muted) {
      await this.pauseListening();
      this.cancelTurnCompletionTimer();
      this.cancelMicSilenceTimer();
      this.cancelFailsafeTimer();
    } else {
      await this.resumeListening();
    }

    this.callStore.setIsMicOn(!muted);
    this.callStore.setUserMicPreference(!muted);
  }

  private clearTranscriptState(): void {
    this.logger.log('[AUDIO_CALL_CONTROL] Clearing transcript state');
    this._currentTranscript = '';
    this._partialTranscript = '';
    this.lastProcessedTranscript = '';
    this.callStore.setSpeechTranscript('');
    this.callStore.setSpeechTranscriptPartial('');
    this.callStore.setRecognizedTranscripts([]);
    this.callStore.setPartialTranscriptExists(false);
  }

  private setState(state: ConversationState): void {
    if (this._state !== state) {
      this.logger.log('[AUDIO_CALL_CONTROL] State change', {
        from: this._state,
        to: state
      });
      this._state = state;
      this._stateChange$.next(state);
    }
  }

  private addMessage(role: 'agent' | 'user', content: string): void {
    this._conversationHistory.push({
      role,
      content,
      timestamp: new Date(),
    });
  }

  private handleAgentStartSpeaking(): void {
    this.logger.log('[AUDIO_CALL_CONTROL] Agent started speaking — audio forwarding suspended via isAvatarSpeaking flag');
    this.stopWatchingTranscripts();
    this.callStore.setIsAvatarSpeaking(true);
    this.setState(ConversationState.AGENT_SPEAKING);
    this.cancelTurnCompletionTimer();
    this.cancelMicSilenceTimer();
    this.cancelFailsafeTimer();
  }

  private async handleAgentStopSpeaking(): Promise<void> {
    // Always reset avatar-speaking flag first — agent stopped speaking regardless of
    // listen-cycle skip or mic preference. Without this, sendAudioData() would
    // permanently discard the user's mic audio for the rest of the call.
    this.logger.log('[AUDIO_CALL_CONTROL] Agent stopped speaking — audio forwarding resumed via isAvatarSpeaking flag');
    this.callStore.setIsAvatarSpeaking(false);
    if (this._isFirstSpeakPending) {
      this._isFirstSpeakPending = false;
      this.logger.log('[AUDIO_CALL_CONTROL] Welcome message done — unlocking PDF gate');
      this._welcomeMessageDone$.next();
    }

    if (this._skipListenCycleOnNextSpeakEnd) {
      this._skipListenCycleOnNextSpeakEnd = false;
      this.logger.log('[AUDIO_CALL_CONTROL] Skipping listen cycle');
      return;
    }
    this.logger.log('[AUDIO_CALL_CONTROL] Agent stopped speaking');
    this._currentAgentSpeech = '';
    this.clearTranscriptState();
    this.startWatchingTranscripts();
    this.setState(ConversationState.LISTENING);
    if (!this.callStore.getUserMicPreference()) {
      this.logger.log('[AUDIO_CALL_CONTROL] Mic muted by user preference, skipping listen cycle');
      this.setState(ConversationState.LISTENING);
    }
  }

  private async pauseListening(): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Pausing STT');
    await this.assemblyService.pause();
  }

  private async resumeListening(): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Resuming STT');
    await this.assemblyService.resume();
  }

  private startTurnCompletionTimer(): void {
    this.cancelTurnCompletionTimer();
    const delay = this.assemblyService.sttConfig?.turnCompletionDelay ?? 0;

    this.logger.log('[AUDIO_CALL_CONTROL] Turn completion timer started', { delay });

    this.turnCompletionTimer = setTimeout(() => {
      this.turnCompletionTimer = null;
      const transcript = this._currentTranscript || this._partialTranscript;
      if (transcript.trim()) {
        this.logger.log('[AUDIO_CALL_CONTROL] Turn completion window expired, processing utterance');
        this.processUserUtterance(transcript.trim());
      }
    }, delay);
  }

  private cancelTurnCompletionTimer(): void {
    if (this.turnCompletionTimer !== null) {
      clearTimeout(this.turnCompletionTimer);
      this.turnCompletionTimer = null;
      this.logger.log('[AUDIO_CALL_CONTROL] Turn completion timer cancelled');
    }
  }

  private handleAudioLevel(rms: number): void {
    if (!this.isWatchingTranscripts) return;

    if (rms >= AudioCallControlService.SILENCE_RMS_THRESHOLD) {
      this.cancelMicSilenceTimer();
      this.cancelFailsafeTimer();
    } else {
      // Only start mic silence timer when there is an accumulated transcript to protect.
      // Without this guard, timers cycle indefinitely while the user is silent with nothing to send.
      const hasTranscript = !!(this._currentTranscript || this._partialTranscript);
      if (this.micSilenceTimer === null && this.failsafeTimer === null && hasTranscript) {
        this.startMicSilenceTimer();
      }
    }
  }

  private startMicSilenceTimer(): void {
    const delay = this.assemblyService.maxTurnSilenceDelay;
    if (!delay) return;

    this.logger.log('[AUDIO_CALL_CONTROL] Mic silence timer started', { delay });
    this.micSilenceTimer = setTimeout(() => {
      this.micSilenceTimer = null;
      // If Story 2.2's turn completion timer is already running, Assembly already
      // responded — let normal flow handle it and skip the failsafe.
      if (this.turnCompletionTimer !== null) {
        this.logger.log('[AUDIO_CALL_CONTROL] Mic silence expired but turn completion already in progress — skipping failsafe');
        return;
      }
      this.logger.log('[AUDIO_CALL_CONTROL] Mic silence detected, starting failsafe timer');
      const transcript = this._currentTranscript || this._partialTranscript;
      if (transcript.trim()) {
        this.startFailsafeTimer();
      }
    }, delay);
  }

  private cancelMicSilenceTimer(): void {
    if (this.micSilenceTimer !== null) {
      clearTimeout(this.micSilenceTimer);
      this.micSilenceTimer = null;
      this.logger.log('[AUDIO_CALL_CONTROL] Mic silence timer cancelled');
    }
  }

  private startFailsafeTimer(): void {
    this.cancelFailsafeTimer();
    const delay = this.assemblyService.failsafeDelay;
    this.logger.log('[AUDIO_CALL_CONTROL] Failsafe timer started', { delay });

    this.failsafeTimer = setTimeout(() => {
      this.failsafeTimer = null;
      const transcript = this._currentTranscript || this._partialTranscript;
      
      if (transcript.trim()) {
        this.logger.warn(
          '[AUDIO_CALL_CONTROL] Failsafe triggered — STT provider did not respond in time',
          {
            transcript: transcript.substring(0, 100),
            maxTurnSilenceDelay: this.assemblyService.maxTurnSilenceDelay,
            failsafeDelay: delay,
            state: this._state,
          },
        );
        this.processUserUtterance(transcript.trim());
      } else {
        this.logger.warn('[AUDIO_CALL_CONTROL] Failsafe triggered but no transcript available — skipping');
      }
    }, delay);
  }

  private cancelFailsafeTimer(): void {
    if (this.failsafeTimer !== null) {
      clearTimeout(this.failsafeTimer);
      this.failsafeTimer = null;
      this.logger.log('[AUDIO_CALL_CONTROL] Failsafe timer cancelled');
    }
  }

  /**
   * Standardized error handling: logs, emits via onError$, and sets ERROR state.
   */
  private handleError(error: Error, context?: string): void {
    const errorMessage = context || 'Error occurred';
    this.logger.error(`[AUDIO_CALL_CONTROL] ${errorMessage}`, error, {
      errorMessage: error.message,
      context,
      state: this._state,
      callUuid: this.callStore.getCallUuid()?.trim() ?? '',
      practiceCallUuid: this.callStore.getPracticeCallUuid()?.trim() ?? '',
    });

    // Halt the turn loop before escalating so no late transcript/timer starts another turn once in error state.
    this.stopWatchingTranscripts();
    this.cancelTurnCompletionTimer();
    this.cancelMicSilenceTimer();
    this.cancelFailsafeTimer();

    this._error$.next(error);
    this.setState(ConversationState.ERROR);
  }

  getGreeting(): string {
    return "Hello! I'm your AI assistant. How can I help you today?";
  }

  /**
   * Set the welcome message to use for the next startCall().
   * Used by pathway when initiatePathwayStepSimulation returns.
   * Pass empty string to clear.
   */
  setWelcomeMessage(text: string): void {
    this._pendingWelcomeMessage = text ?? '';
  }

  /**
   * Call this before playing TTS that bypasses the normal agent-speak flow
   * (e.g. quiz result outcome audio). The next `onSpeakingEnd$` emission will be
   * ignored so the service does not resume the STT listener.
   */
  skipListenCycleOnNextSpeakEnd(): void {
    this._skipListenCycleOnNextSpeakEnd = true;
  }

  ngOnDestroy(): void {
    this.cancelTurnCompletionTimer();
    this.cancelMicSilenceTimer();
    this.cancelFailsafeTimer();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.cleanupEffects();
    this._stateChange$.complete();
    this._agentMessage$.complete();
    this._userMessage$.complete();
    this._transcriptUpdate$.complete();
    this._captionText$.complete();
    this._error$.complete();
    this._callEnded$.complete();
    this._welcomeMessageDone$.complete();
    this.logger.clearFileContext();
  }
}
